import HttpStatus from "@cayo/shared/api/types/HttpStatus";
import { processErrorResponse } from "@cayo/shared/utils/ajax";

class UserProfileClient<T> {
  private http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(
    baseUrl?: string,
    http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }
  ) {
    this.http = http ? http : (window as any);
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  async getElements(elementClass: string): Promise<string[] | null> {
    let url_ = this.baseUrl + "/profile/{elementClass}";
    if (elementClass === undefined || elementClass === null) {
      throw new Error("The parameter 'elementClass' must be defined.");
    }
    url_ = url_.replace("{elementClass}", encodeURIComponent("" + elementClass));
    url_ = url_.replace(/[?&]$/, "");

    const options_: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    const _response = await this.http.fetch(url_, options_);
    return await this.processGetElements(_response);
  }

  protected async processGetElements(response: Response): Promise<string[] | null> {
    const status = response.status;
    if (status === HttpStatus.OK) {
      const _responseText = await response.text();
      return _responseText === ""
        ? null
        : (JSON.parse(_responseText, this.jsonParseReviver) as string[]);
    }
    if (status !== HttpStatus.NO_CONTENT) {
      return processErrorResponse(response, status);
    }
    return Promise.resolve<string[] | null>(null as any);
  }

  async getElement(elementClass: string, name: string): Promise<T | null> {
    let url_ = this.baseUrl + "/profile/{elementClass}/{name}";
    if (elementClass === undefined || elementClass === null) {
      throw new Error("The parameter 'elementClass' must be defined.");
    }
    url_ = url_.replace("{elementClass}", encodeURIComponent("" + elementClass));
    if (name === undefined || name === null) {
      throw new Error("The parameter 'name' must be defined.");
    }
    url_ = url_.replace("{name}", encodeURIComponent("" + name));
    url_ = url_.replace(/[?&]$/, "");

    const options_: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    const _response = await this.http.fetch(url_, options_);
    return await this.processGetElement(_response);
  }

  protected async processGetElement(response: Response): Promise<T | null> {
    const status = response.status;
    if (status === HttpStatus.OK) {
      const _responseText = await response.text();
      return _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as T);
    }
    if (status !== HttpStatus.NO_CONTENT) {
      return processErrorResponse(response, status);
    }
    return Promise.resolve<T | null>(null as any);
  }

  async saveElement(elementClass: string, name: string, scheme: any): Promise<T | null> {
    let url_ = this.baseUrl + "/profile/{elementClass}/{name}";
    if (elementClass === undefined || elementClass === null) {
      throw new Error("The parameter 'elementClass' must be defined.");
    }
    url_ = url_.replace("{elementClass}", encodeURIComponent("" + elementClass));
    if (name === undefined || name === null) {
      throw new Error("The parameter 'name' must be defined.");
    }
    url_ = url_.replace("{name}", encodeURIComponent("" + name));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(scheme);

    const options_: RequestInit = {
      body: content_,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    const _response = await this.http.fetch(url_, options_);
    return await this.processSaveElement(_response);
  }

  protected async processSaveElement(response: Response): Promise<T | null> {
    const status = response.status;
    if (status === HttpStatus.OK) {
      const _responseText = await response.text();
      return _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as T);
    }
    if (status !== HttpStatus.NO_CONTENT) {
      return processErrorResponse(response, status);
    }
    return Promise.resolve<T | null>(null as any);
  }

  async deleteElement(elementClass: string, name: string): Promise<void> {
    let url_ = this.baseUrl + "/profile/{elementClass}/{name}";
    if (elementClass === undefined || elementClass === null) {
      throw new Error("The parameter 'elementClass' must be defined.");
    }
    url_ = url_.replace("{elementClass}", encodeURIComponent("" + elementClass));
    if (name === undefined || name === null) {
      throw new Error("The parameter 'name' must be defined.");
    }
    url_ = url_.replace("{name}", encodeURIComponent("" + name));
    url_ = url_.replace(/[?&]$/, "");

    const options_: RequestInit = {
      method: "DELETE",
      headers: {},
    };

    const _response = await this.http.fetch(url_, options_);
    return await this.processDeleteElement(_response);
  }

  protected processDeleteElement(response: Response): Promise<void> {
    const status = response.status;
    if (status === HttpStatus.OK || status === HttpStatus.NO_CONTENT) {
      return Promise.resolve();
    }
    return processErrorResponse(response, status);
  }
}

export default UserProfileClient;
